import { useLingui } from "@lingui/react";
import { useModals } from "@mobility-item-page/dealer/client";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { Button } from "@warp-ds/elements/react/button";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { type ContactTopic, getTopicContent } from "./contactTopics.ts";
import { evContactActionClicked } from "./evTrackingEvents.ts";

type Props = {
  topic: ContactTopic;
};

/**
 * "Ask the seller" link used inside the EV info expandables. Dealer ads open the dealer contact
 * flow, private ads use the messaging podlet — the same one behind the main "Send message" button.
 */
export const ContactAction = ({ topic }: Props) => {
  const { i18n } = useLingui();
  const { isLoggedIn, isPreview } = useAppConfig();
  const { openContactFlow } = useModals();
  const { ownerUrn } = useAdViewMeta();
  const { adViewType } = useAdViewAd();
  const { trackClick } = usePulse();

  const { label, trackingObjectPosition, message } = getTopicContent(topic, i18n, adViewType);

  if (isPreview) {
    return <Button variant="link">{label}</Button>;
  }

  if (isPrivateAdvertiser(ownerUrn)) {
    // The podlet fires M_ev01 itself, so we deliberately do not — a second one would double-count
    // the lead. It has no position prop, so the expandable lands in `object.name` instead. Signed
    // off as such, rather than lose private-ad leads from the reporting entirely.
    // NOTE: finn-contact-button has no prefill prop yet (v1.0.130) — its destination URL comes from
    // the podlet's own #contact-button-data blob. Hand it `message` once it does.
    // <w-button full-width> is hardcoded inside the podlet, so keep the link from filling the card
    return (
      <div className="w-fit">
        {/* @ts-expect-error TODO: fix the custom element typing */}
        <finn-contact-button buttonVariant="link" buttonText={label} trackingObjectName={trackingObjectPosition} />
      </div>
    );
  }

  const openContactForm = () => {
    // Tracking event: M_ev01. Fired before the login redirect below, so the click is not lost to
    // the navigation
    trackClick(evContactActionClicked(trackingObjectPosition));

    if (!isLoggedIn) {
      const { origin, pathname } = window.location;
      const loginUrl = new URL(`${origin}/auth/login`);
      loginUrl.searchParams.set("redirectUrl", `${origin}${pathname}?contact=${encodeURIComponent(topic)}`);
      loginUrl.searchParams.set("cancelUrl", `${origin}${pathname}`);
      window.location.href = loginUrl.toString();
      return;
    }
    openContactFlow({ prefilledMessage: message, contactTopic: topic });
  };

  return (
    <Button variant="link" onClick={openContactForm}>
      {label}
    </Button>
  );
};
